import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, LogOut, LayoutDashboard, Package, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n";
import { LOGO_URL } from "../lib/api";

function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div data-testid="lang-toggle" className="inline-flex items-center rounded-full nb-border nb-shadow-sm overflow-hidden bg-white">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-head font-bold text-sm transition-colors ${
            lang === l ? "bg-nbblack text-white" : "text-nbblack hover:bg-nbblack/5"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FFF9F0]/85 backdrop-blur-md border-b-[3px] border-nbblack">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[110px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={LOGO_URL} alt="2T Sticker" className="h-[74px] sm:h-24 w-auto" />
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          <LangToggle />
          <Link
            to="/design"
            data-testid="nav-design"
            className={`hidden sm:inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press ${
              location.pathname === "/design" ? "bg-nbyellow" : "bg-white"
            }`}
          >
            <Sparkles size={16} /> {t("nav.create")}
          </Link>

          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="hidden sm:inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press bg-nbcyan"
            >
              <LayoutDashboard size={16} /> {t("nav.admin")}
            </Link>
          )}

          {user && (
            <Link
              to="/designs"
              data-testid="nav-designs"
              className={`hidden sm:inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press ${
                location.pathname === "/designs" ? "bg-nbyellow" : "bg-white"
              }`}
            >
              <Sparkles size={16} /> {t("nav.designs")}
            </Link>
          )}

          {user && (
            <Link
              to="/orders"
              data-testid="nav-orders"
              className="hidden sm:inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press bg-white"
            >
              <Package size={16} /> {t("nav.orders")}
            </Link>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press bg-white"
          >
            <ShoppingCart size={18} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-nbpink text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center nb-border"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <button
              onClick={onLogout}
              data-testid="nav-logout"
              className="inline-flex items-center gap-1.5 font-head font-bold px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press bg-nbblack text-white"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">{t("nav.logout")}</span>
            </button>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-login"
              className="inline-flex items-center font-head font-bold px-5 py-2 rounded-full nb-border nb-shadow-sm nb-press bg-nbpink text-white"
            >
              {t("nav.login")}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
